import { Howl, Howler } from 'howler';
import _ from 'lodash';
import store from 'store';
import {
  SET_PLAYING,
  SET_CORRECT_SOUNDS_PLAYED,
  SET_INCORRECT_SOUNDS_PLAYED,
} from 'actions/types';
import { audioURL } from 'config';
import getLocaleAudioFolderName from 'utils/getLocaleAudioFolderName';
import { resolveOfflineAssetUrlSync } from 'mobile-modules/offline';

const NUMBER_OF_CORRECT_SOUNDS = 7;
const NUMBER_OF_INCORRECT_SOUNDS = 6;
const correctSoundStrings = [1392, 1393, 1394, 1395, 1396, 1397, 1398];
const incorrectSoundStrings = [635, 1400, 1401, 1402, 1403, 1404];
const WATCH_STRING_NUMBER = 1405;
const AUDIO_LOAD_WATCHDOG_MS = 4500;
const AUDIO_END_GRACE_MS = 750;


const resolveActivityPlaybackLocale = (state, locales = []) => {
  const activityLocaleCode = String(
    state.flinkMake?.activity?.editingActivity?.activity?.currentLanguageVersion?.locale
    || state.flinkPlay?.activityLocale?.code
    || ''
  ).trim().toLowerCase();
  if (activityLocaleCode) {
    const locale = _.find(
      locales,
      item => String(item?.code || '').trim().toLowerCase() === activityLocaleCode
    );
    if (locale) return locale;
  }
  return state.flinkPlay?.solutionLocale
    || _.find(locales, { code: 'en' })
    || locales[0]
    || { code: 'en', name: 'English' };
};

const setPlaying = (audioSrc, isPlaying) => {
  store.dispatch({
    filter: 'audio',
    type: SET_PLAYING,
    payload: { audioSrc, isPlaying },
  });
};

const getStringAudioPath = (
  editingActivity,
  solutionLocale,
  strings,
  locales,
  stringNumber
) => {
  let audioPath = '';
  const activityLocale = editingActivity
    ? (
        editingActivity.activity.currentLanguageVersion.locale
          ? editingActivity.activity.currentLanguageVersion.locale
          : 'en'
      )
    : 'en';
  const locale = solutionLocale
    || _.find(locales, { code: activityLocale });
  const search = _.find(
    strings,
    item => Number(item?.stringNumber) === Number(stringNumber)
  );
  const localizedString = _.get(search?.data, locale?.code);
  const audioFilename = localizedString?.stringAudio || localizedString?.audio;

  if (audioFilename) {
    const localeFolder = getLocaleAudioFolderName(locale);
    audioPath = `${audioURL}/Generic/Strings/${localeFolder}/${audioFilename}`;
  }

  return audioPath;
};

const resolveStandardFeedbackAudio = isCorrect => {
  const state = store.getState();
  const audio = state.audio || {};
  const strings = state.common?.strings || [];
  const locales = state.common?.locales || [];
  const editingActivity = state.flinkMake?.activity?.editingActivity;
  const solutionLocale = resolveActivityPlaybackLocale(state, locales);

  if (isCorrect) {
    const correctSoundsPlayed =
      NUMBER_OF_CORRECT_SOUNDS === audio.correctSoundsPlayed
        ? 1
        : (audio.correctSoundsPlayed || 0) + 1;

    const audioPath = getStringAudioPath(
      editingActivity,
      solutionLocale,
      strings,
      locales,
      correctSoundStrings[correctSoundsPlayed - 1]
    );

    const src = audioPath || `${audioURL}/Generic/Feedback/${
      solutionLocale ? solutionLocale.name : 'English'
    }/correct${correctSoundsPlayed}.mp3`;

    store.dispatch({
      filter: 'audio',
      type: SET_CORRECT_SOUNDS_PLAYED,
      payload: correctSoundsPlayed,
    });

    return src;
  }

  const incorrectSoundsPlayed =
    NUMBER_OF_INCORRECT_SOUNDS === audio.incorrectSoundsPlayed
      ? 1
      : (audio.incorrectSoundsPlayed || 0) + 1;

  const audioPath = getStringAudioPath(
    editingActivity,
    solutionLocale,
    strings,
    locales,
    incorrectSoundStrings[incorrectSoundsPlayed - 1]
  );

  const src = audioPath || `${audioURL}/Generic/Feedback/${
    solutionLocale ? solutionLocale.name : 'English'
  }/incorrect${incorrectSoundsPlayed}.mp3`;

  store.dispatch({
    filter: 'audio',
    type: SET_INCORRECT_SOUNDS_PLAYED,
    payload: incorrectSoundsPlayed,
  });

  return src;
};


const resolveWatchAudio = () => {
  const state = store.getState();
  const strings = state.common?.strings || [];
  const locales = state.common?.locales || [];
  const editingActivity = state.flinkMake?.activity?.editingActivity;
  const solutionLocale = resolveActivityPlaybackLocale(state, locales);

  const audioPath = getStringAudioPath(
    editingActivity,
    solutionLocale,
    strings,
    locales,
    WATCH_STRING_NUMBER
  );

  return audioPath || `${audioURL}/Generic/Feedback/${
    solutionLocale ? solutionLocale.name : 'English'
  }/watch.mp3`;
};

class MobileFindTheOneAudioPlayer {
  constructor() {
    this.sound = null;
    this.src = '';
    this.requestId = 0;
    this.watchdogTimer = null;
  }

  clearWatchdog = () => {
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  };

  scheduleWatchdog = (requestId, timeoutMs, finish) => {
    this.clearWatchdog();
    if (!timeoutMs || timeoutMs <= 0) return;

    this.watchdogTimer = setTimeout(() => {
      if (requestId !== this.requestId) return;
      finish(new Error('mobile-audio-watchdog'));
    }, timeoutMs);
  };

  stop = () => {
    this.requestId += 1;
    this.clearWatchdog();

    if (this.sound) {
      try {
        this.sound.stop();
        this.sound.unload();
      } catch (error) {
        console.warn('[FindTheOne][mobileAudio] stop failed', error);
      }
    }

    if (this.src) setPlaying(this.src, false);
    this.sound = null;
    this.src = '';
  };

  toggle = src => {
    if (!src) return;

    if (this.src === src && this.sound) {
      if (this.sound.playing()) {
        this.sound.pause();
        setPlaying(src, false);
      } else {
        this.sound.play();
        setPlaying(src, true);
      }
      return;
    }

    this.playSrc(src);
  };

  playSrc = (src, options = {}) => {
    if (!src) {
      options.onEnd && options.onEnd({ src: '', error: 'missing-src' });
      return '';
    }

    this.stop();

    const requestId = ++this.requestId;
    this.src = src;

    // Prime WebAudio while the React-DnD touch/drop event is still active.
    // This is local to Find The One and does not alter window.audioPlayer.
    if (Howler.ctx?.state === 'suspended') {
      try {
        Howler.ctx.resume().catch(() => {});
      } catch (error) {
        // HTML5 playback below remains the primary native path.
      }
    }

    let finished = false;
    let fallbackStarted = false;

    const finish = error => {
      if (finished || requestId !== this.requestId) return;
      finished = true;
      this.clearWatchdog();
      setPlaying(src, false);

      if (error && this.sound) {
        try {
          this.sound.stop();
          this.sound.unload();
        } catch (stopError) {
          // Progress must not depend on cleanup succeeding.
        }
      }

      console.log('[FindTheOne][mobileAudio] end', {
        src,
        error: error?.message || error || '',
      });

      options.onEnd && options.onEnd({ src, error });
    };

    const startHowl = useHtml5 => {
      if (requestId !== this.requestId) return;

      if (this.sound) {
        try {
          this.sound.unload();
        } catch (error) {
          // Continue with the replacement Howl instance.
        }
      }

      const retryOrFinish = error => {
        if (requestId !== this.requestId || finished) return;

        if (useHtml5 && !fallbackStarted) {
          fallbackStarted = true;

          console.warn('[FindTheOne][mobileAudio] HTML5 failed, retry WebAudio', {
            src,
            error: error?.message || error || '',
          });

          const retry = () => startHowl(false);

          if (Howler.ctx?.state === 'suspended') {
            try {
              Howler.ctx.resume().then(retry).catch(finish);
            } catch (resumeError) {
              finish(resumeError);
            }
          } else {
            retry();
          }
          return;
        }

        finish(error);
      };

      const resolvedSrc = (Array.isArray(src) ? src : [src])
        .map((value) => resolveOfflineAssetUrlSync(value));

      this.sound = new Howl({
        src: resolvedSrc,
        html5: useHtml5,
        onload: () => {
          if (requestId !== this.requestId || finished) return;
          const durationSeconds = Number(this.sound?.duration?.()) || 0;
          const timeoutMs = durationSeconds > 0
            ? Math.max(3000, Math.ceil(durationSeconds * 1000) + AUDIO_END_GRACE_MS)
            : AUDIO_LOAD_WATCHDOG_MS;
          this.scheduleWatchdog(requestId, timeoutMs, finish);
        },
        onplay: () => {
          if (requestId !== this.requestId) return;
          setPlaying(src, true);
          console.log('[FindTheOne][mobileAudio] playing', {
            src,
            backend: useHtml5 ? 'html5' : 'webaudio',
          });
        },
        onend: () => finish(),
        onloaderror: (soundId, error) => retryOrFinish(error),
        onplayerror: (soundId, error) => retryOrFinish(error),
      });

      try {
        // Important: called synchronously from Find The One's native drop/click path.
        // If iOS never sends load/end/error, the watchdog still releases the game flow.
        this.scheduleWatchdog(requestId, AUDIO_LOAD_WATCHDOG_MS, finish);
        this.sound.play();
        setPlaying(src, true);
      } catch (error) {
        retryOrFinish(error);
      }
    };

    console.log('[FindTheOne][mobileAudio] start', { src });
    startHowl(true);
    return src;
  };

  playStandardFeedback = (isCorrect, options = {}) => {
    const src = resolveStandardFeedbackAudio(isCorrect);
    return this.playSrc(src, options);
  };

  playWatch = (options = {}) => {
    const src = resolveWatchAudio();
    return this.playSrc(src, options);
  };
}

const mobileFindTheOneAudioPlayer = new MobileFindTheOneAudioPlayer();

export const playMobileFindTheOneStandardFeedback = (
  isCorrect,
  options = {}
) => mobileFindTheOneAudioPlayer.playStandardFeedback(isCorrect, options);

export const playMobileFindTheOneAudio = (src, options = {}) =>
  mobileFindTheOneAudioPlayer.playSrc(src, options);

export const playMobileFindTheOneWatch = (options = {}) =>
  mobileFindTheOneAudioPlayer.playWatch(options);

export const toggleMobileFindTheOneAudio = src =>
  mobileFindTheOneAudioPlayer.toggle(src);

export const stopMobileFindTheOneAudio = () =>
  mobileFindTheOneAudioPlayer.stop();

export default mobileFindTheOneAudioPlayer;
